import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/feedback";
import { useAccessToken } from "@/hooks";
import { BalanceApi } from "@/openapi/generated";
import { createAuthenticatedApi, getApiErrorMessage } from "@/lib/utils";
import { invalidateBalanceQueries } from "@/features/balance/utils/invalidate-balance-queries";

export const useBalanceDetailDelete = (
  toast: ReturnType<typeof useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (unitDetailId: number) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(BalanceApi, accessToken);

      await service.apiBalanceDetailsIdDelete({
        id: unitDetailId,
      });
    },
    onSuccess: async () => {
      await invalidateBalanceQueries(queryClient);
      toast({
        title: "Draft deleted",
        description: "The draft balance line was removed.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error: unknown) => {
      const errorMessage = await getApiErrorMessage(
        error,
        "Failed to delete draft balance",
      );
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isSuccess, isError };
};
